import productFamilyRoles from '@enums/product-family-roles';
import rewardTypes from '@enums/reward-types';

const hasMotherProductKey = product =>
    product?.MotherProductKey !== undefined && product?.MotherProductKey !== null;

const isSelfReferencing = product =>
    hasMotherProductKey(product) &&
    String(product.MotherProductKey) === String(product.clientProductKey);

const isMotherRole = role => role === productFamilyRoles.mother;

const buildProductFamilyIndex = products => {
    const childrenByParentKey = new Map();

    products.forEach(product => {
        if (!hasMotherProductKey(product) || isSelfReferencing(product)) return;

        const parentKey = String(product.MotherProductKey);
        if (!childrenByParentKey.has(parentKey)) childrenByParentKey.set(parentKey, []);
        childrenByParentKey.get(parentKey).push(product);
    });

    return { childrenByParentKey };
};

// Promotion products do not persist MotherProductKey, so take it from the catalogue product.
const withMotherProductKeys = (products, catalogueProducts) => {
    const motherKeysByProductKey = new Map(
        catalogueProducts
            .filter(hasMotherProductKey)
            .map(product => [String(product.productKey), product.MotherProductKey])
    );

    return products.map(product =>
        hasMotherProductKey(product) || !motherKeysByProductKey.has(String(product.productKey))
            ? product
            : {
                  ...product,
                  MotherProductKey: motherKeysByProductKey.get(String(product.productKey)),
              }
    );
};

const getChildren = (product, index) =>
    index.childrenByParentKey.get(String(product.clientProductKey)) || [];

const getProductFamilyRole = product => {
    if (isSelfReferencing(product)) return productFamilyRoles.mother;
    if (hasMotherProductKey(product)) return productFamilyRoles.child;
    return productFamilyRoles.orphan;
};

const getProductFamily = (product, index) => {
    if (!product) return [];
    if (isMotherRole(getProductFamilyRole(product))) {
        return [product, ...getChildren(product, index)];
    }
    return [product];
};

const getProductFamilyOnOfferGroup = (product, index, productKeysOnOfferGroup) =>
    getProductFamily(product, index).filter(member =>
        productKeysOnOfferGroup.has(member.productKey)
    );

const getPriceUpdateProducts = ({
    product,
    index,
    productKeysOnOfferGroup,
    motherProductSelectionEnabled,
    reward,
}) => {
    if (
        !motherProductSelectionEnabled ||
        reward !== rewardTypes.newPricePerItem ||
        !isMotherRole(getProductFamilyRole(product))
    ) {
        return [product];
    }

    return getProductFamilyOnOfferGroup(product, index, productKeysOnOfferGroup);
};

const getMotherProductOptions = (products, index) =>
    products
        .filter(product => isMotherRole(getProductFamilyRole(product)))
        .map(product => {
            const familySize = getProductFamily(product, index).length;
            return {
                productKey: product.productKey,
                clientProductKey: product.clientProductKey,
                name: product.name,
                familySize,
                label: `${product.name} (${product.clientProductKey}) (${familySize})`,
            };
        });

const getMotherSelectionChanges = (previousKeys, nextKeys) => {
    const previous = new Set(previousKeys.map(String));
    const next = new Set(nextKeys.map(String));

    return {
        toSelect: nextKeys.filter(key => !previous.has(String(key))),
        toDeselect: previousKeys.filter(key => !next.has(String(key))),
    };
};

const getPartiallySelectedMotherKeys = (mothers, getVisibleChildren, isSelected) =>
    new Set(
        mothers
            .filter(mother => {
                const children = getVisibleChildren(mother);
                const selectedCount = children.filter(child => isSelected(child.productKey)).length;
                return selectedCount > 0 && selectedCount < children.length;
            })
            .map(mother => String(mother.productKey))
    );

export {
    buildProductFamilyIndex,
    getMotherProductOptions,
    getMotherSelectionChanges,
    getPartiallySelectedMotherKeys,
    getPriceUpdateProducts,
    getProductFamily,
    getProductFamilyOnOfferGroup,
    getProductFamilyRole,
    hasMotherProductKey,
    isMotherRole,
    productFamilyRoles,
    withMotherProductKeys,
};
